import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Target, ArrowLeft, Trophy, Users, CheckCircle, Clock, MapPin, Hash, UserPlus, AlertTriangle, Timer } from "lucide-react";

const API = '/api';

// Countdown Timer Component
function CountdownTimer({ endDate, onExpire }) {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0, expired: false });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const end = new Date(endDate).getTime();
      const difference = end - now;

      if (difference <= 0) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0, expired: true });
        if (onExpire) onExpire();
        return;
      }

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({ hours, minutes, seconds, expired: false });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [endDate, onExpire]);

  if (timeLeft.expired) {
    return <span className="text-red-600 font-medium">Appeal Period Expired</span>;
  }

  return (
    <div className="flex items-center gap-1 font-mono text-lg">
      <Timer size={18} className="text-yellow-600" />
      <span className="bg-yellow-100 px-2 py-1 rounded">{String(timeLeft.hours).padStart(2, '0')}</span>
      <span>:</span>
      <span className="bg-yellow-100 px-2 py-1 rounded">{String(timeLeft.minutes).padStart(2, '0')}</span>
      <span>:</span>
      <span className="bg-yellow-100 px-2 py-1 rounded">{String(timeLeft.seconds).padStart(2, '0')}</span>
    </div>
  );
}

export default function Matches() {
  const { eventId } = useParams();
  const { isAdmin, getAuthHeader } = useAuth();
  const [event, setEvent] = useState(null);
  const [matches, setMatches] = useState([]);
  const [shooters, setShooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shooterDialogOpen, setShooterDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchEntries, setMatchEntries] = useState([]);
  const [selectedShooters, setSelectedShooters] = useState([]);
  const [editingMatch, setEditingMatch] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    match_number: "",
    entry_fee: "",
    shots_per_round: "",
    track_streak: true,
    is_team_event: false,
    trap_range: "",
    start_datetime: "",
    end_datetime: "",
    appeal_duration_hours: "24",
    is_championship: false,
    source_match_ids: [],
  });

  const fetchData = useCallback(async () => {
    try {
      const [eventRes, matchesRes, shootersRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`),
        axios.get(`${API}/matches?event_id=${eventId}`),
        axios.get(`${API}/shooters`),
      ]);
      setEvent(eventRes.data);
      setMatches(matchesRes.data);
      setShooters(shootersRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        event_id: eventId,
        name: formData.name,
        match_number: formData.match_number || null,
        entry_fee: parseFloat(formData.entry_fee) || 0,
        shots_per_round: parseInt(formData.shots_per_round) || 25,
        track_streak: formData.track_streak,
        is_team_event: formData.is_team_event,
        trap_range: formData.trap_range || null,
        start_datetime: formData.start_datetime || null,
        end_datetime: formData.end_datetime || null,
        appeal_duration_hours: parseInt(formData.appeal_duration_hours) || 24,
        is_championship: formData.is_championship,
        source_match_ids: formData.is_championship ? formData.source_match_ids : null,
      };

      if (editingMatch) {
        await axios.put(`${API}/matches/${editingMatch.id}`, payload, {
          headers: getAuthHeader()
        });
        toast.success("Match updated");
      } else {
        await axios.post(`${API}/matches`, payload, {
          headers: getAuthHeader()
        });
        toast.success("Match created");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save match");
    }
  };

  const handleStatusToggle = async (match) => {
    const newStatus = match.status === "active" ? "completed" : "active";
    try {
      await axios.patch(`${API}/matches/${match.id}/status`, { status: newStatus }, {
        headers: getAuthHeader()
      });
      toast.success(`Match marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleStartAppeal = async (match) => {
    try {
      await axios.patch(`${API}/matches/${match.id}/status`, { status: "appeal_period" }, {
        headers: getAuthHeader()
      });
      toast.success("Appeal period started");
      fetchData();
    } catch (error) {
      toast.error("Failed to start appeal period");
    }
  };

  const handleCloseMatch = async (match) => {
    try {
      await axios.patch(`${API}/matches/${match.id}/status`, { status: "closed" }, {
        headers: getAuthHeader()
      });
      toast.success("Match closed");
      fetchData();
    } catch (error) {
      toast.error("Failed to close match");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this match and all its data?")) return;
    try {
      await axios.delete(`${API}/matches/${id}`, {
        headers: getAuthHeader()
      });
      toast.success("Match deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete match");
    }
  };

  // Shooter management functions
  const openShooterDialog = async (match) => {
    setSelectedMatch(match);
    setSelectedShooters([]);
    try {
      const res = await axios.get(`${API}/entries?match_id=${match.id}`);
      setMatchEntries(res.data);
      // Pre-select shooters who are already in the match
      const existingShooterIds = res.data.map(e => e.shooter_id);
      setSelectedShooters(existingShooterIds);
    } catch (error) {
      console.error("Failed to fetch entries");
      setMatchEntries([]);
    }
    setShooterDialogOpen(true);
  };

  const handleShooterToggle = (shooterId) => {
    setSelectedShooters(prev => {
      if (prev.includes(shooterId)) {
        return prev.filter(id => id !== shooterId);
      } else {
        return [...prev, shooterId];
      }
    });
  };

  const handleAddShooters = async () => {
    if (!selectedMatch) return;
    
    // Find shooters to add (selected but not in matchEntries)
    const existingShooterIds = matchEntries.map(e => e.shooter_id);
    const shootersToAdd = selectedShooters.filter(id => !existingShooterIds.includes(id));
    
    try {
      // Add new shooters
      for (const shooterId of shootersToAdd) {
        await axios.post(`${API}/entries`, {
          match_id: selectedMatch.id,
          shooter_id: shooterId,
        }, {
          headers: getAuthHeader()
        });
      }
      
      toast.success(`Added ${shootersToAdd.length} shooter(s) to match`);
      setShooterDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add shooters");
    }
  };

  const handleRemoveFromMatch = async (entryId) => {
    if (!window.confirm("Remove this shooter from the match?")) return;
    try {
      await axios.delete(`${API}/entries/${entryId}`, {
        headers: getAuthHeader()
      });
      toast.success("Shooter removed from match");
      // Refresh the dialog
      if (selectedMatch) {
        openShooterDialog(selectedMatch);
      }
    } catch (error) {
      toast.error("Failed to remove shooter");
    }
  };

  const openEditDialog = (match) => {
    setEditingMatch(match);
    setFormData({
      name: match.name,
      match_number: match.match_number || "",
      entry_fee: match.entry_fee.toString(),
      shots_per_round: match.shots_per_round.toString(),
      track_streak: match.track_streak,
      is_team_event: match.is_team_event,
      trap_range: match.trap_range || "",
      start_datetime: match.start_datetime || "",
      end_datetime: match.end_datetime || "",
      appeal_duration_hours: (match.appeal_duration_hours || 24).toString(),
      is_championship: match.is_championship || false,
      source_match_ids: match.source_match_ids ? match.source_match_ids.split(',') : [],
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingMatch(null);
    setFormData({
      name: "",
      match_number: "",
      entry_fee: "",
      shots_per_round: "25",
      track_streak: true,
      is_team_event: false,
      trap_range: "",
      start_datetime: "",
      end_datetime: "",
      appeal_duration_hours: "24",
      is_championship: false,
      source_match_ids: [],
    });
  };

  const formatDateTime = (dt) => {
    if (!dt) return null;
    try {
      return new Date(dt).toLocaleString();
    } catch {
      return dt;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="matches-page" className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/events" className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2">
          <ArrowLeft size={14} /> Back to Events
        </Link>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
              {event?.name}
            </h1>
            <p className="text-slate-500 mt-1">
              {event?.start_date} - {event?.end_date} • {matches.length} matches
            </p>
          </div>

          {isAdmin() && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  onClick={() => { resetForm(); setDialogOpen(true); }}
                  className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                  data-testid="create-match-btn"
                >
                  <Plus size={20} className="mr-2" />
                  Add Match
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[550px]">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl">
                    {editingMatch ? "Edit Match" : "Add Match"}
                  </DialogTitle>
                  <DialogDescription>
                    Matches are the actual shooting competitions
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="match-name">Match Name</Label>
                      <Input
                        id="match-name"
                        data-testid="match-name-input"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., 50-Bird Trap Championship"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="match-number">Match Number</Label>
                      <Input
                        id="match-number"
                        value={formData.match_number}
                        onChange={(e) => setFormData({ ...formData, match_number: e.target.value })}
                        placeholder="e.g., M001, 1"
                      />
                      <p className="text-xs text-slate-500 mt-1">For search/identification</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="entry-fee">Entry Fee ($)</Label>
                      <Input
                        id="entry-fee"
                        type="number"
                        step="0.01"
                        min="0"
                        data-testid="match-entry-fee"
                        value={formData.entry_fee}
                        onChange={(e) => setFormData({ ...formData, entry_fee: e.target.value })}
                        placeholder="25.00"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="shots">Shots/Round</Label>
                      <Input
                        id="shots"
                        type="number"
                        min="1"
                        max="500"
                        data-testid="match-shots"
                        value={formData.shots_per_round}
                        onChange={(e) => setFormData({ ...formData, shots_per_round: e.target.value })}
                        placeholder="25"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="trap-range">Trap Range</Label>
                      <Input
                        id="trap-range"
                        data-testid="match-trap-range"
                        value={formData.trap_range}
                        onChange={(e) => setFormData({ ...formData, trap_range: e.target.value })}
                        placeholder="Range 1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start-dt">Start Date/Time</Label>
                      <Input
                        id="start-dt"
                        type="datetime-local"
                        value={formData.start_datetime}
                        onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="end-dt">End Date/Time</Label>
                      <Input
                        id="end-dt"
                        type="datetime-local"
                        value={formData.end_datetime}
                        onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-2">
                    <div>
                      <Label htmlFor="track_streak">Track Longest Streak</Label>
                      <p className="text-sm text-slate-500">Show consecutive hits</p>
                    </div>
                    <Switch
                      id="track_streak"
                      checked={formData.track_streak}
                      onCheckedChange={(checked) => setFormData({ ...formData, track_streak: checked })}
                    />
                  </div>

                  <div className="flex items-center justify-between py-2 border-t pt-4">
                    <div>
                      <Label htmlFor="is_team_event" className="flex items-center gap-2">
                        <Users size={18} className="text-[#1B3B6F]" />
                        Team Match
                      </Label>
                      <p className="text-sm text-slate-500">Group scores by team</p>
                    </div>
                    <Switch
                      id="is_team_event"
                      checked={formData.is_team_event}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_team_event: checked })}
                    />
                  </div>

                  <div className="border-t pt-4">
                    <Label htmlFor="appeal_hours">Appeal Period (hours)</Label>
                    <Input
                      id="appeal_hours"
                      type="number"
                      min="0"
                      value={formData.appeal_duration_hours}
                      onChange={(e) => setFormData({ ...formData, appeal_duration_hours: e.target.value })}
                      placeholder="24"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Time allowed for result appeals after match closes
                    </p>
                  </div>

                  {/* Aggregate Match Section */}
                  <div className="border-t pt-4 bg-gradient-to-r from-yellow-50 to-amber-50 -mx-6 px-6 pb-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Trophy size={20} className="text-yellow-600" />
                        <div>
                          <Label htmlFor="is_championship" className="text-base font-semibold">Aggregate Match</Label>
                          <p className="text-xs text-slate-500">Combine best scores from multiple matches</p>
                        </div>
                      </div>
                      <Switch
                        id="is_championship"
                        data-testid="aggregate-toggle"
                        checked={formData.is_championship}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_championship: checked, source_match_ids: checked ? formData.source_match_ids : [] })}
                      />
                    </div>
                    
                    {formData.is_championship && (
                      <div className="mt-3 p-3 bg-white rounded-lg border-2 border-yellow-300 shadow-sm">
                        <Label className="text-sm font-medium text-yellow-800 mb-2 block">
                          Select matches to combine ({formData.source_match_ids.length} selected):
                        </Label>
                        <div className="max-h-40 overflow-y-auto space-y-2">
                          {matches.filter(m => !m.is_championship && m.id !== editingMatch?.id).map(m => (
                            <label key={m.id} className="flex items-center gap-2 cursor-pointer hover:bg-yellow-100 p-2 rounded border border-transparent hover:border-yellow-200">
                              <Checkbox
                                checked={formData.source_match_ids.includes(m.id) || formData.source_match_ids.includes(String(m.id))}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setFormData({ ...formData, source_match_ids: [...formData.source_match_ids, m.id] });
                                  } else {
                                    setFormData({ ...formData, source_match_ids: formData.source_match_ids.filter(id => id !== m.id && String(id) !== String(m.id)) });
                                  }
                                }}
                              />
                              <span className="text-sm font-medium">{m.name}</span>
                              {m.shots_per_round && <span className="text-xs text-slate-400">({m.shots_per_round} shots)</span>}
                            </label>
                          ))}
                          {matches.filter(m => !m.is_championship && m.id !== editingMatch?.id).length === 0 && (
                            <p className="text-sm text-yellow-600 italic">No other matches available to combine. Create regular matches first.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <Button type="submit" className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90">
                    {editingMatch ? "Update Match" : "Create Match"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Matches List */}
      {matches.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">No matches yet. {isAdmin() ? "Add your first match!" : "Check back soon."}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {matches.map((match) => (
            <Card key={match.id} className={`card-hover ${match.is_championship ? 'border-2 border-yellow-400 bg-yellow-50/30' : ''}`} data-testid={`match-card-${match.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {match.is_championship && (
                        <Badge className="bg-yellow-500 text-yellow-900">
                          <Trophy size={10} className="mr-1" />
                          Aggregate
                        </Badge>
                      )}
                      {match.match_number && (
                        <Badge variant="outline" className="font-mono text-xs">
                          <Hash size={10} className="mr-1" />
                          {match.match_number}
                        </Badge>
                      )}
                      <h3 className="font-serif text-lg font-semibold text-slate-800">
                        {match.name}
                      </h3>
                      <Badge
                        variant={match.status === "active" ? "default" : "secondary"}
                        className={
                          match.status === "active" ? "bg-green-100 text-green-800" : 
                          match.status === "appeal_period" ? "bg-yellow-100 text-yellow-800" :
                          match.status === "closed" ? "bg-slate-100 text-slate-600" :
                          "bg-slate-100 text-slate-600"
                        }
                      >
                        {match.status === "active" && <Clock size={10} className="mr-1" />}
                        {match.status === "appeal_period" && <AlertTriangle size={10} className="mr-1" />}
                        {(match.status === "completed" || match.status === "closed") && <CheckCircle size={10} className="mr-1" />}
                        {match.status === "appeal_period" ? "Appeal Period" : match.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {match.is_team_event && (
                        <Badge variant="outline" className="text-xs">
                          <Users size={10} className="mr-1" /> Team
                        </Badge>
                      )}
                      {match.trap_range && (
                        <Badge variant="outline" className="text-xs">
                          <MapPin size={10} className="mr-1" /> {match.trap_range}
                        </Badge>
                      )}
                      {match.is_championship && match.source_match_ids && (
                        <Badge variant="outline" className="text-xs bg-yellow-100">
                          <Target size={10} className="mr-1" /> {match.source_match_ids.split(',').length} matches combined
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Aggregate Match - Show calculated scores link */}
                {match.is_championship && (
                  <div className="mb-3 p-3 bg-yellow-100 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      This aggregate match combines best scores from selected matches.
                    </p>
                    <Link 
                      to={`/match-results/${match.id}`}
                      className="text-sm font-medium text-yellow-700 hover:text-yellow-900 underline"
                    >
                      View Aggregate Results →
                    </Link>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                  {isAdmin() && !match.is_championship && (
                    <div>
                      <p className="text-slate-500">Entry Fee</p>
                      <p className="font-mono text-lg font-bold text-green-700">${match.entry_fee}</p>
                    </div>
                  )}
                  {!match.is_championship && (
                    <div>
                      <p className="text-slate-500">Shots/Round</p>
                      <p className="font-mono text-lg font-bold text-[#1B3B6F]">{match.shots_per_round}</p>
                    </div>
                  )}
                </div>

                {!match.is_championship && (match.start_datetime || match.end_datetime) && (
                  <div className="text-xs text-slate-500 mb-3 border-t pt-2">
                    {match.start_datetime && <p>Start: {formatDateTime(match.start_datetime)}</p>}
                    {match.end_datetime && <p>End: {formatDateTime(match.end_datetime)}</p>}
                  </div>
                )}

                {match.status === "appeal_period" && match.appeal_end_date && (
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded-lg mb-3">
                    <p className="font-medium text-sm mb-2 flex items-center gap-2">
                      <AlertTriangle size={16} />
                      Appeal Period Active
                    </p>
                    <CountdownTimer 
                      endDate={match.appeal_end_date} 
                      onExpire={() => fetchData()}
                    />
                    <p className="text-xs text-yellow-600 mt-2">
                      Ends: {new Date(match.appeal_end_date).toLocaleString()}
                    </p>
                  </div>
                )}

                {match.appeal_duration_hours && match.status !== "appeal_period" && (
                  <div className="text-xs text-slate-400 mb-3 flex items-center gap-1">
                    <Timer size={12} />
                    Appeal Period: {match.appeal_duration_hours}h
                  </div>
                )}

                <div className="flex gap-2 flex-wrap pt-3 border-t">
                  <Link to={`/score-entry/${match.id}`}>
                    <Button size="sm" className="bg-green-700 hover:bg-green-600">
                      <Target size={14} className="mr-1" /> Score Entry
                    </Button>
                  </Link>
                  <Link to={`/leaderboard/${match.id}`}>
                    <Button size="sm" variant="outline">
                      <Trophy size={14} className="mr-1" /> Leaderboard
                    </Button>
                  </Link>
                  {isAdmin() && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="bg-blue-50 hover:bg-blue-100 text-blue-700"
                        onClick={() => openShooterDialog(match)}
                      >
                        <UserPlus size={14} className="mr-1" /> Add Shooters
                      </Button>
                      {match.status === "active" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusToggle(match)}
                        >
                          Complete
                        </Button>
                      )}
                      {match.status === "completed" && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="bg-yellow-50 hover:bg-yellow-100 text-yellow-700"
                            onClick={() => handleStartAppeal(match)}
                          >
                            <AlertTriangle size={14} className="mr-1" /> Start Appeal
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStatusToggle(match)}
                          >
                            Reactivate
                          </Button>
                        </>
                      )}
                      {match.status === "appeal_period" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-slate-100 hover:bg-slate-200"
                          onClick={() => handleCloseMatch(match)}
                        >
                          Close Match
                        </Button>
                      )}
                      {match.status === "closed" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleStatusToggle(match)}
                        >
                          Reactivate
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(match)}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(match.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Quick links */}
      <div className="flex gap-4">
        <Link to={`/event-leaderboards/${eventId}`}>
          <Button variant="outline">
            <Trophy size={18} className="mr-2" /> View All Leaderboards
          </Button>
        </Link>
        <Link to={`/shootoff-board/${eventId}`}>
          <Button variant="outline">
            <Target size={18} className="mr-2" /> Shoot-Off Board
          </Button>
        </Link>
      </div>

      {/* Add Shooters Dialog */}
      <Dialog open={shooterDialogOpen} onOpenChange={setShooterDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <UserPlus size={20} /> Manage Shooters
            </DialogTitle>
            <DialogDescription>
              {selectedMatch?.name} - Add or view shooters in this match
            </DialogDescription>
          </DialogHeader>
          
          {/* Currently registered shooters */}
          {matchEntries.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold text-sm text-slate-700 mb-2">
                Registered Shooters ({matchEntries.length})
              </h4>
              <div className="space-y-2 max-h-[150px] overflow-y-auto border rounded-lg p-2 bg-slate-50">
                {matchEntries.map(entry => {
                  const shooter = shooters.find(s => s.id === entry.shooter_id);
                  return (
                    <div key={entry.id} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div>
                        <span className="font-medium">{shooter?.name || 'Unknown'}</span>
                        {shooter?.shooter_id && (
                          <span className="text-xs text-slate-500 ml-2">#{shooter.shooter_id}</span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50 h-7"
                        onClick={() => handleRemoveFromMatch(entry.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add new shooters */}
          <div>
            <h4 className="font-semibold text-sm text-slate-700 mb-2">
              Add Shooters
            </h4>
            <div className="space-y-2 max-h-[250px] overflow-y-auto border rounded-lg p-2">
              {shooters.length === 0 ? (
                <p className="text-slate-500 text-sm p-2">No shooters found. Add shooters first.</p>
              ) : (
                shooters
                  .filter(s => !matchEntries.some(e => e.shooter_id === s.id))
                  .map(shooter => (
                    <div
                      key={shooter.id}
                      className={`flex items-center gap-3 p-2 rounded border cursor-pointer transition-colors ${
                        selectedShooters.includes(shooter.id)
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-white hover:bg-slate-50'
                      }`}
                      onClick={() => handleShooterToggle(shooter.id)}
                    >
                      <Checkbox
                        checked={selectedShooters.includes(shooter.id)}
                        onCheckedChange={() => handleShooterToggle(shooter.id)}
                      />
                      <div className="flex-1">
                        <span className="font-medium">{shooter.name}</span>
                        {shooter.shooter_id && (
                          <span className="text-xs text-slate-500 ml-2">#{shooter.shooter_id}</span>
                        )}
                      </div>
                    </div>
                  ))
              )}
              {shooters.filter(s => !matchEntries.some(e => e.shooter_id === s.id)).length === 0 && shooters.length > 0 && (
                <p className="text-slate-500 text-sm p-2">All shooters are already registered to this match.</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShooterDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
              onClick={handleAddShooters}
              disabled={selectedShooters.filter(id => !matchEntries.some(e => e.shooter_id === id)).length === 0}
            >
              <UserPlus size={16} className="mr-2" />
              Add Selected ({selectedShooters.filter(id => !matchEntries.some(e => e.shooter_id === id)).length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
